import React from 'react';
import { PlaceContext } from '@m4a3/api-client';
import Page from '../../components/Page/Page';
import PlaceDropPointSelector from '../../components/PlaceDropPointSelector';

const PlaceDropPointTest = () => {
  const handleDropPointSelect = (
    dropPointId: string, 
    lat: number, 
    lng: number, 
    placeContext: PlaceContext
  ) => {
    console.log('Drop point selected:', {
      dropPointId,
      lat,
      lng,
      placeContext,
      timestamp: new Date()
    });
  };

  return (
    <Page title="Place Drop Point Selector Test">
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Place Drop Point Selector Test
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              Test the PlaceDropPointSelector component by entering Irish Eircodes to view and select drop points.
            </p>
          </div>

          {/* Component Demo - showing different modes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 200px width demo - Read Only */}
            <div className="w-48 border-2 border-dashed border-blue-300 p-2">
              <div className="text-xs text-blue-600 mb-2 text-center">Compact Read-Only</div>
              <PlaceDropPointSelector
                onDropPointSelect={handleDropPointSelect}
                title="Drop Point Selector"
                subtitle="Read-only selection"
                initialEircode="D15TF8Y"
                isCompact={true}
              />
            </div>

            {/* 200px width demo - With Modal Option */}
            <div className="w-48 border-2 border-dashed border-purple-300 p-2">
              <div className="text-xs text-purple-600 mb-2 text-center">Compact + Modal</div>
              <PlaceDropPointSelector
                onDropPointSelect={handleDropPointSelect}
                title="Drop Point Selector"
                subtitle="Click expand for full view"
                initialEircode="D15TF8Y"
                isCompact={true}
                enableModalExpansion={true}
              />
            </div>
            
            {/* Regular width - With management features */}
            <div className="border-2 border-dashed border-green-300 p-4">
              <div className="text-xs text-green-600 mb-2 text-center">Full Management</div>
              <PlaceDropPointSelector
                onDropPointSelect={handleDropPointSelect}
                title="Delivery Spot Management"
                subtitle="Full management capabilities"
                initialEircode="D15TF8Y"
                enableManagement={true}
              />
            </div>
          </div>



          {/* Feature Documentation */}
          <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Management Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Available Actions</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <strong>Add Pin:</strong> + button in drop points panel header
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <strong>Add Note:</strong> + button in notes panel header
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <strong>Enable/Disable:</strong> Click the status pill next to place name
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                    <strong>Show Deleted:</strong> Checkbox in drop points panel
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <strong>Set Preferred:</strong> Button appears when drop point is selected
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <strong>Delete:</strong> Button appears when drop point is selected
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Usage Modes</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><strong>Compact Read-Only:</strong> <code>isCompact={`{true}`}</code> - 200px width, selection only</li>
                  <li><strong>Compact + Modal:</strong> <code>enableModalExpansion={`{true}`}</code> - expand to full management</li>
                  <li><strong>Full Management:</strong> <code>enableManagement={`{true}`}</code> - complete CRUD operations</li>
                  <li><strong>Auto-Managed:</strong> Modal state handled internally, no external setup needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default PlaceDropPointTest;
